import React, { Fragment } from "react";
import Page from "../components/page";
import Image from "@vorne/gatsby-theme-marketing/components/image/image";
import { graphql } from "gatsby";
import BigIdea from "@vorne/gatsby-theme-marketing/components/bigidea/bigIdea";
import PageTitle from "@vorne/gatsby-theme-marketing/components/pagetitle/pageTitle";
import QuickLinks from "@vorne/gatsby-theme-marketing/components/quicklinks/quickLinks";
import {
  Body1,
  Heading1,
} from "@vorne/gatsby-theme-marketing/components/typography/typography";
import List, {
  ListItem,
} from "@vorne/gatsby-theme-marketing/components/list/v1";
import Routes from "../routes";
import Link from "@vorne/gatsby-theme-marketing/components/link/link";
import CallToActionButton from "@vorne/gatsby-theme-marketing/components/button/calltoaction/callToAction";
import Section from "@vorne/gatsby-theme-marketing/components/section/section";
import Center from "@vorne/gatsby-theme-marketing/components/layout/center/center";
import { Seo } from "@vorne/gatsby-theme-marketing/components/seo/v2";
import { OEEBanner } from "@vorne/gatsby-theme-marketing/components/banners";

const PracticalOEE = ({ data }) => {
  return (
    <Page>
      <Section>
        <PageTitle>Practical OEE</PageTitle>

        <BigIdea>
          ​It is easy to get hung up on the technical details of OEE. From a
          practical perspective you will go a long way towards a successful
          implementation by ensuring your Ideal Cycle Times are accurate,
          focusing on incremental improvement, and paying close attention to the
          losses that impact OEE.
        </BigIdea>
        <QuickLinks tag="h2" />
        <Heading1 id="oeeFundamentals">OEE Fundamentals</Heading1>
        <Body1>
          Let's start with some OEE fundamentals. OEE measures how close you are
          to perfect production (manufacturing only good parts, as fast as
          possible, with no down time). It’s calculated from{" "}
          <Link url={Routes.OEE.Factors}>three underlying factors</Link>:
          Availability, Performance, and Quality.
        </Body1>
        <List type="bullet">
          <ListItem>
            An <strong>Availability</strong> score of 100% means the process is
            always running during planned production time (it's never down). The
            losses that impact Availability include downtime, changeovers, and
            maintenance.{" "}
          </ListItem>
          <ListItem>
            A <strong>Performance</strong> score of 100% means when the process
            is running it is running as fast as possible (every cycle at the
            Ideal Cycle Time). The losses that impact Performance include slow
            cycles and small stops.
          </ListItem>
          <ListItem>
            A <strong>Quality</strong> score of 100% means there are no defects
            (only good parts are produced). The losses that impact Quality
            include all forms of rejects that occur during the first
            manufacturing pass (including parts that can be reworked).
          </ListItem>
        </List>
        <OEEBanner />
        <Body1>OEE is calculated by multiplying the three factors.</Body1>
        <Center $horizontally>
          <Image
            src={data.calculatingOeeImage}
            alt="Chart of A times P times Q equals OEE"
            renderCaption={() => <Fragment></Fragment>}
          />
        </Center>
        <Heading1 id="theBiggestOeeMistake">The Biggest OEE Mistake</Heading1>
        <Body1>
          By far the most common mistake we see with{" "}
          <Link url={Routes.OEE.ImplementingOEE}>OEE implementations</Link> is
          inaccurate Ideal Cycle Times. For example, many companies use ‘budget’
          or ‘standard’ speeds that are slower than the Ideal Cycle Time. Doing
          so inflates OEE, hides loss, and underestimates the true throughput of
          the process.
        </Body1>
        <Body1>
          Ideal Cycle Time (or ICT) is the theoretical maximum throughput of the
          process. It should not be lowered due to factors such as machine age
          or material quality. Instead, these types of loss factors should be
          seen as opportunities for improvement.
        </Body1>
        <Body1>
          Two common approaches to determining Ideal Cycle Time are:
        </Body1>
        <List type="bullet">
          <ListItem>
            Nameplate Capacity: This is the value that the equipment builder
            specifies (e.g., you may buy a press with a design capacity of 120
            strokes per minute).
          </ListItem>
          <ListItem>
            Time Study: Measure the absolute fastest speed that the process can
            support (not an average or normal speed). Be careful not to include
            any form of speed loss (i.e., slow cycles or small stops).
          </ListItem>
        </List>
        <Body1>
          Here is a fun exercise! Can you identify which parts below have an
          inaccurate ICT? If your answer is Part D and Part H - you are exactly
          right. An Ideal Cycle Time that is higher than the Run Cycle Time is
          set too high. In the real world, we see this problem all the time.
        </Body1>
        <Body1>
          What about Part J? It may simply be a part that ran poorly with a lot
          of slow cycles. However, it may also be a part where the ICT is set
          too low. As an outlier it is definitely worth a bit of investigation.
        </Body1>
        <Center $horizontally>
          <Link url={Routes.VorneWebsiteRoute}>
            <Image
              src={data.idealCycleTimeImage}
              alt="Bar chart showing ideal cycle time and average run cycle time by part."
              renderCaption={() => (
                <Fragment>
                  This chart exposes inaccurate Ideal Cycle Times by comparing
                  Ideal Cycle Times to actual cycle times on a part-by-part
                  basis.
                </Fragment>
              )}
            />
          </Link>
        </Center>
        <Heading1 id="theOeeScore">The OEE Score</Heading1>
        <Body1>What is a good OEE score?</Body1>
        <Body1>
          Let’s start with what it’s not. It is not an arbitrary number like the
          frequently cited{" "}
          <Link url={Routes.OEE.WorldClassOEE}>85% world-class OEE</Link>.
          Instead, a good OEE score is one that is consistently improving.
          Determine your baseline OEE score and focus on steadily and
          incrementally improving from that point. We can’t emphasize this
          enough. It’s about percentage improved - NOT percentage achieved.
        </Body1>
        <Center $horizontally>
          <Image
            src={data.tipOeeScoreImage}
            alt="Image of Tip: A good OEE score is one that shows steady, incremental, and sustained improvement. It's about percentage improved, not percentage achieved."
            renderCaption={() => <Fragment></Fragment>}
          />
        </Center>
        <Body1>
          We recommend that you accurately determine your OEE baseline for each
          process and set targets that drive meaningful and incremental
          improvement. Each step should be a stretch target that is achievable
          within a couple of months. Short enough to keep people engaged, long
          enough to achieve significant improvement.
        </Body1>
        <Body1>
          This is effectively an{" "}
          <Link url={Routes.Vorne.Agile}>agile approach</Link> to improvement.
          Agile is by nature incremental - breaking large tasks, goals, and
          projects into small deliverables. The principles of agile apply very
          well to manufacturing improvement projects and OEE.
        </Body1>
        <Body1>
          One more tip - let OEE be a brutal measure. It’s okay to wince a
          little when you see your “true” OEE score. See it as an opportunity. A
          lot of companies exclude bits and pieces of time from OEE - all with
          very reasonable justifications. Don’t give into temptation. The more
          losses that are exposed, the more fertile ground you’ll have for
          improvement.
        </Body1>
        <Center $horizontally>
          <Image
            src={data.oeeOpportunityImage}
            alt="Image of OEE concept as a simple rule: If time can be used for value-added production, include it in OEE. Consider Including Changeovers, no operators, rework parts, early shift starts, prototype runs, shift handovers, maintenance, quality control inspection, and training."
            renderCaption={() => <Fragment></Fragment>}
          />
        </Center>
        <Heading1 id="oeeAsAnEffectiveTool">OEE as an Effective Tool</Heading1>
        <Body1>Why measure OEE?</Body1>
        <Body1>
          It is easy to get so focused on your OEE score that you lose sight of
          why you are measuring it. Think of OEE as a tool to further your
          business goals. Usually that means focusing less on the OEE score and
          more on the underlying{" "}
          <Link url={Routes.OEE.SixBigLosses}>OEE losses</Link>. Monitoring OEE
          on its own is not all that helpful to improving production.
        </Body1>
        <Body1>
          Let’s look at monitoring OEE from three perspectives - operator,
          supervisor, and loss.
        </Body1>
        <Body1>
          From an <strong>operator perspective</strong> - OEE is a somewhat
          abstract measure. While you certainly can set an OEE target for
          operators and even monitor it in real time, an excellent alternative
          is to focus on Efficiency (measured as the ratio of Good Count to
          Target Count). The benefit of Efficiency is that it enables operators
          to “win their shift” by achieving 100% of their target (i.e., 100%
          Efficiency). This is a very powerful motivator – especially when
          operators can see their Efficiency score in real time.
        </Body1>
        <Body1>
          Another benefit of focusing on Efficiency is that it is calculated
          from Takt Time. This enables setting an explicit target for operators,
          while management keeps their focus on the true capabilities of the
          process based on Ideal Cycle Time.
        </Body1>
        <Center $horizontally>
          <Link url={Routes.VorneWebsiteRoute}>
            <Image
              src={data.andonImage}
              alt="Andon display showing Stamping 7 at 101.6% efficiency and Forging 3 at 103.2% efficiency."
              renderCaption={() => <Fragment></Fragment>}
            />
          </Link>
        </Center>
        <Body1>
          From a <strong>supervisor perspective</strong> - OEE is a very useful
          top-level analytical tool, especially when viewed as a trend that
          shows whether or not the process is improving over time. As an
          analytical tool, it is very important to break OEE into the three
          underlying factors: Availability, Performance, and Quality. These
          three factors are the bridge to understanding the underlying losses
          that impact OEE.
        </Body1>
        <Center $horizontally>
          <Link url={Routes.VorneWebsiteRoute}>
            <Image
              src={data.kpisImage}
              alt="Trend lines for OEE, Availability, Performance, and Quality."
              renderCaption={() => <Fragment></Fragment>}
            />
          </Link>
        </Center>
        <Body1>
          From a <strong>loss perspective</strong> - OEE is the starting point
          for <strong>taking action</strong>. Your OEE score tells you where you
          are at - but not what you need to do to improve. The real power and
          the true value of OEE comes from understanding and acting on the
          underlying losses: Availability Loss, Performance Loss, and Quality
          Loss. Take effective action to reduce these losses and your top-level
          OEE score will naturally improve.
        </Body1>
        <Body1>
          The three types of OEE losses can be mapped to six universal loss
          categories (also referred to as the{" "}
          <Link url={Routes.Vorne.SixBigLosses}>Six Big Losses</Link>). From
          there it becomes much easier to apply specific tools and actions to
          address the losses.
        </Body1>
        <Center $horizontally>
          <Image
            src={data.oeeToolImage}
            alt="Info chart showing OEE as a Tool to help you monitor and act."
            renderCaption={() => <Fragment></Fragment>}
          />
        </Center>
        <Center $horizontally>
          <CallToActionButton url={Routes.OEE.FreeTools}>
            Download Free OEE Tools
          </CallToActionButton>
        </Center>
      </Section>
    </Page>
  );
};

export default PracticalOEE;

export function Head() {
  return (
    <Seo
      title="Practical OEE - Learn Fundamentals and Avoid Mistakes"
      description="Learn about the fundamentals of OEE and how to avoid the most common OEE mistake with this in-depth guide.."
    />
  );
}

export const query = graphql`
  query {
    calculatingOeeImage: file(
      relativePath: { eq: "calculating-oee-image.png" }
    ) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, width: 916)
      }
    }
    idealCycleTimeImage: file(
      relativePath: { eq: "ideal-cycle-time-image.png" }
    ) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, width: 916)
      }
    }
    tipOeeScoreImage: file(relativePath: { eq: "tip-oee-score-image.png" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, width: 600)
      }
    }
    oeeOpportunityImage: file(
      relativePath: { eq: "oee-opportunity-image.png" }
    ) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, width: 600)
      }
    }
    andonImage: file(relativePath: { eq: "andon-image.png" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, width: 916)
      }
    }
    kpisImage: file(relativePath: { eq: "kpis-image.png" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, width: 916)
      }
    }
    oeeToolImage: file(relativePath: { eq: "oee-tool-image.png" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, width: 600)
      }
    }
  }
`;
